import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Input, Label, FormGroup, Alert
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const BASE_URL = "https://harmanagelapi.stechomeyazilim.info";

const SliderYonetimi = () => {
  document.title = "Slider Yönetimi | HarmanaGEL";

  const [sliderlar, setSliderlar] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [modal, setModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ ID: null, BASLIK: "", GORSEL_YOLU: "", SIRA: "", AKTIF: true });

  const toggleModal = () => {
    setModal(!modal);
    if (modal) {
      setFormData({ ID: null, BASLIK: "", GORSEL_YOLU: "", SIRA: "", AKTIF: true });
      setIsEditing(false);
    }
  };

  const fetchSliderlar = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${BASE_URL}/SLIDER_GORSELLERI?$orderby=SIRA asc`);
      const result = await response.json();
      if (result && result.value) setSliderlar(result.value);
    } catch (err) {
      setError("Slider görselleri yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSliderlar();
  }, []);

  const handleEdit = (slider) => {
    setFormData({
      ID: slider.ID,
      BASLIK: slider.BASLIK || "",
      GORSEL_YOLU: slider.GORSEL_YOLU || "",
      SIRA: slider.SIRA || "",
      AKTIF: slider.AKTIF !== false,
    });
    setIsEditing(true);
    setModal(true);
  };

  const handleSave = async () => {
    if (!formData.GORSEL_YOLU.trim()) {
      setError("Görsel yolu zorunludur.");
      return;
    }
    try {
      const payload = {
        BASLIK: formData.BASLIK,
        GORSEL_YOLU: formData.GORSEL_YOLU,
        SIRA: parseInt(formData.SIRA) || 0,
        AKTIF: formData.AKTIF,
      };
      if (isEditing) {
        await fetch(`${BASE_URL}/SLIDER_GORSELLERI(${formData.ID})`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        setSuccessMsg("Slider güncellendi.");
      } else {
        await fetch(`${BASE_URL}/SLIDER_GORSELLERI`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...payload, ID: Date.now() }),
        });
        setSuccessMsg("Slider eklendi.");
      }
      toggleModal();
      fetchSliderlar();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setError("Kayıt sırasında hata oluştu.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu slider görselini silmek istediğinize emin misiniz?")) return;
    try {
      await fetch(`${BASE_URL}/SLIDER_GORSELLERI(${id})`, { method: "DELETE" });
      setSuccessMsg("Slider silindi.");
      fetchSliderlar();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setError("Silme sırasında hata oluştu.");
    }
  };

  const columns = useMemo(() => [
    {
      header: "Sıra",
      accessorKey: "SIRA",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => <Badge color="light" className="text-dark fs-6 px-3">{cell.getValue() || "-"}</Badge>,
    },
    {
      header: "Görsel",
      accessorKey: "GORSEL_YOLU",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => {
        const url = cell.getValue();
        return url ? (
          <img
            src={url}
            alt="Slider"
            style={{ height: 50, width: 120, objectFit: "cover", borderRadius: 4 }}
            onError={(e) => { e.target.src = "https://via.placeholder.com/120x50?text=Görsel"; }}
          />
        ) : <span className="text-muted">Görsel yok</span>;
      },
    },
    {
      header: "Başlık",
      accessorKey: "BASLIK",
      enableSorting: true,
      enableColumnFilter: true,
      cell: (cell) => cell.getValue() || <span className="text-muted">Başlıksız</span>,
    },
    {
      header: "Görsel URL",
      accessorKey: "GORSEL_YOLU",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => (
        <span className="text-muted small" style={{ maxWidth: 200, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {cell.getValue()}
        </span>
      ),
    },
    {
      header: "Durum",
      accessorKey: "AKTIF",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => (
        <Badge color={cell.getValue() ? "success" : "secondary"}>
          {cell.getValue() ? "Aktif" : "Pasif"}
        </Badge>
      ),
    },
    {
      header: "İşlemler",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cellProps) => {
        const s = cellProps.row.original;
        return (
          <div className="d-flex gap-2">
            <Button color="primary" size="sm" outline onClick={() => handleEdit(s)}>
              <i className="bx bx-edit" />
            </Button>
            <Button color="danger" size="sm" outline onClick={() => handleDelete(s.ID)}>
              <i className="bx bx-trash" />
            </Button>
          </div>
        );
      },
    },
  ], []);

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="HarmanaGEL" breadcrumbItem="Slider Yönetimi" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="card-title mb-0">Slider Görselleri</h4>
                    <Button color="primary" size="sm" onClick={() => { setIsEditing(false); setModal(true); }}>
                      <i className="bx bx-plus me-1" />
                      Yeni Slider
                    </Button>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={sliderlar}
                      isGlobalFilter={false}
                      isPagination={true}
                      isCustomPageSize={true}
                      tableClass="table-bordered align-middle nowrap w-100 mb-0"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal isOpen={modal} toggle={toggleModal} centered>
        <ModalHeader toggle={toggleModal}>
          {isEditing ? "Slider Düzenle" : "Yeni Slider Ekle"}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Görsel URL *</Label>
            <Input
              value={formData.GORSEL_YOLU}
              onChange={(e) => setFormData({ ...formData, GORSEL_YOLU: e.target.value })}
              placeholder="https://..."
            />
          </FormGroup>
          <FormGroup>
            <Label>Başlık</Label>
            <Input
              value={formData.BASLIK}
              onChange={(e) => setFormData({ ...formData, BASLIK: e.target.value })}
              placeholder="Slider başlığı"
            />
          </FormGroup>
          <FormGroup>
            <Label>Sıra No</Label>
            <Input
              type="number"
              value={formData.SIRA}
              onChange={(e) => setFormData({ ...formData, SIRA: e.target.value })}
              placeholder="1"
            />
          </FormGroup>
          <FormGroup check>
            <Input
              type="checkbox"
              checked={formData.AKTIF}
              onChange={(e) => setFormData({ ...formData, AKTIF: e.target.checked })}
            />
            <Label check>Aktif</Label>
          </FormGroup>
          {formData.GORSEL_YOLU && (
            <div className="mt-3">
              <Label>Önizleme:</Label>
              <img
                src={formData.GORSEL_YOLU}
                alt="Önizleme"
                className="img-fluid rounded border mt-1"
                style={{ maxHeight: 120, width: "100%", objectFit: "cover" }}
                onError={(e) => { e.target.style.display = "none"; }}
              />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={toggleModal}>İptal</Button>
          <Button color="primary" onClick={handleSave}>
            {isEditing ? "Güncelle" : "Kaydet"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default SliderYonetimi;
